/**
 * Location data does not originate from the Admin API, yet.
 * It is hardcoded into a data file for now, but may eventually
 * be promoted to a full Admin API-driven feature.
 */

import locations from "../../data/locations.json";

/**
 * Action types
 * @property {string} LOCATION_ERROR
 * @property {string} LOCATION_LOADING
 * @property {string} LOCATION_SUCCESS
 */
export const types = {
  LOCATION_ERROR: "LOCATION_ERROR",
  LOCATION_LOADING: "LOCATION_LOADING",
  LOCATION_SUCCESS: "LOCATION_SUCCESS",
};

/**
 * @namespace Reducers
 */
export const reducers = {
  /**
   * Updates locations state
   * @param {object} state
   * @param {object} action
   * @param {string} action.type
   * @param {array} action.locations
   * @memberof Reducers
   */
  locations: (state = [], action) => {
    switch (action.type) {
      case types.LOCATION_SUCCESS:
        return action.locations;
      default:
        return state;
    }
  },

  /**
   * Updates locations count state—
   * used to assess if locations need to be fetched
   * @param {object} state
   * @param {object} action
   * @param {string} action.type - `LOCATION_SUCCESS`, or no change
   * @param {array} action.locations
   * @memberof Reducers
   */
  locationsCount: (state = 0, action) => {
    switch (action.type) {
      case types.LOCATION_SUCCESS:
        return action.locations.length;
      default:
        return state;
    }
  },

  /**
   * Updates location error state
   * @param {string} state
   * @param {object} action
   * @param {string} action.type
   * @param {string} action.error
   * @memberof Reducers
   */
  locationsError: (state = null, action) => {
    switch (action.type) {
      case types.LOCATION_ERROR:
        return action.error;
      case types.LOCATION_SUCCESS:
        return null;
      default:
        return state;
    }
  },

  /**
   * Updates locations loading state
   * @param {bool} state
   * @param {object} action
   * @param {string} action.type
   * @memberof Reducers
   */
  locationsLoading: (state = false, action) => {
    switch (action.type) {
      case types.LOCATION_ERROR:
      case types.LOCATION_SUCCESS:
        return false;
      case types.LOCATION_LOADING:
        return true;
      default:
        return state;
    }
  },
};

export const actions = {
  /**
   * Fetch locations
   * @memberof Actions
   */
  fetch: () => dispatch => {
    dispatch(actions.handleLoading());

    return new Promise((resolve, reject) => {
      dispatch(actions.handleSuccess(locations.data));

      resolve({
        success: true,
        locations: locations,
      });
    });
  },

  /**
   * Handle locations loading events
   * @memberof Actions
   */
  handleLoading: () => ({ type: types.LOCATION_LOADING }),

  /**
   * Handle locations error messages
   * @param {string} message
   * @memberof Actions
   */
  handleError: message => ({
    type: types.LOCATION_ERROR,
    message,
  }),

  /**
   * Handle locations response
   * @param {array} listing
   * @memberof Actions
   */
  handleSuccess: locations => ({
    type: types.LOCATION_SUCCESS,
    locations: locations,
  }),

  /**
   * Lookup location
   * @param {string} slug
   * @memberof Actions
   */
  lookup: slug => (dispatch, getState) => {
    return new Promise((resolve, reject) => {
      if (!slug) {
        reject({
          success: false,
        });
      }

      const { locations } = getState();
      let location;

      if (!!locations.length) {
        location = locations.find(loc => loc.slug === slug);
      }

      if (!!location) {
        resolve({
          success: true,
          location: location,
        });
      } else if (!!locations[0]) {
        resolve({
          success: true,
          location: locations[0],
        });
      } else {
        reject({
          success: false,
        });
      }
    });
  },
};
